import { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  Popper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';

const StyledPopper = (props) => (
  <Popper
    {...props}
    placement="bottom-start"
    style={{ ...props.style, width: '220px', zIndex: 9999 }}
    disablePortal={false}
  />
);

const OfficerInChargeEdit = ({
  row,
  employeesList,
  changeOfficerInChargeHandler,
  permissions,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(null);

  const inputRef = useRef(null);

  useEffect(() => {
    const employee = employeesList.find((item) => item.id == row.employee);
    setValue(employee);
  }, [employeesList, row]);

  useEffect(() => {
    setTimeout(() => {
      if (editMode && inputRef.current) {
        inputRef.current?.focus();
        setOpen(true);
      }
    });
  }, [editMode]);

  const canAssign = useMemo(
    () => permissions.can_assign_officer,
    [permissions]
  );

  const displayName = row.name
    ? `${row.name}${row.rank ? ` (${row.rank})` : ''}`
    : '';

  return !editMode ? (
    value ? (
      <Tooltip title={displayName} arrow placement="top">
        <Box
          onClick={() => {
            if (canAssign) setEditMode(true);
          }}
          sx={{
            cursor: canAssign ? 'pointer' : 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            overflow: 'hidden',
            maxWidth: '100%',
            px: 0.5,
            py: 0.25,
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: canAssign ? '#f0f4ff' : 'transparent',
            },
          }}
        >
          <PersonIcon sx={{ fontSize: 16, color: '#888', flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: '0.8rem',
              color: '#333',
              fontWeight: 500,
              whiteSpace: 'normal',
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}
          >
            {displayName}
          </Typography>
        </Box>
      </Tooltip>
    ) : (
      canAssign &&
      employeesList.length > 0 && (
        <Tooltip title="Assign Officer" arrow>
          <IconButton
            size="small"
            sx={{
              border: '1px dashed #ccc',
              width: 28,
              height: 28,
              '&:hover': {
                borderColor: '#BF1D2D',
                backgroundColor: '#fef2f2',
              },
            }}
            onClick={() => {
              setEditMode(true);
            }}
          >
            <AddIcon fontSize="small" sx={{ color: '#888' }} />
          </IconButton>
        </Tooltip>
      )
    )
  ) : (
    <Autocomplete
      options={employeesList}
      size="small"
      fullWidth
      PopperComponent={StyledPopper}
      value={value}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onChange={(_, newValue) => {
        changeOfficerInChargeHandler(row.incident_id, newValue?.id || null);
        setValue(newValue);
        setEditMode(false);
      }}
      onBlur={() => {
        setEditMode(false);
      }}
      onKeyDown={(e) => {
        if (e.key === ' ') {
          e.stopPropagation();
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search..."
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '0.8rem',
              height: '34px',
            },
          }}
          onKeyDown={(e) => {
            if (e.key === ' ') {
              e.stopPropagation();
            }
          }}
          inputRef={inputRef}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} style={{ fontSize: '0.82rem', padding: '6px 12px' }}>
          {option.label}
        </li>
      )}
    />
  );
};

export default memo(OfficerInChargeEdit);
